/**
 * Mirror of apps/api/src/lib/video-tags.ts. Nine strings, low churn —
 * cheaper to duplicate than to fetch a constants endpoint. If you add
 * a tag, update both files.
 */
export const VIDEO_TAGS = [
  'prayer',
  'discipleship',
  'family',
  'marriage',
  'parenting',
  'suffering',
  'worship',
  'evangelism',
  'scripture',
] as const;
export type VideoTag = (typeof VIDEO_TAGS)[number];

export const TAG_LABEL: Record<VideoTag, string> = {
  prayer: 'Prayer',
  discipleship: 'Discipleship',
  family: 'Family',
  marriage: 'Marriage',
  parenting: 'Parenting',
  suffering: 'Suffering & Hope',
  worship: 'Worship',
  evangelism: 'Sharing the Gospel',
  scripture: 'Scripture',
};

export type VideoAudience = 'adult' | 'youth' | 'both';

export interface VideoRow {
  videoId: string;
  title: string;
  url: string;
  uploadDate: string | null;
  tags: string[];
  audience: VideoAudience;
}

export interface VideoCategory {
  /** A known theme tag, or 'untagged' for videos with no tags. */
  tag: VideoTag | 'untagged';
  label: string;
  videos: VideoRow[];
}

/**
 * Group videos into theme categories for the Go Deeper catalog. A video
 * with multiple tags appears under each of them; tag-less videos collect
 * under "Untagged". Categories follow VIDEO_TAGS order with Untagged
 * last, and empty categories are omitted so the UI never shows a
 * dead-end. Unknown tags (e.g. one retired from VIDEO_TAGS) don't get
 * their own category — a video surfaces only via its known tags, exactly
 * as the previous inline grouping behaved.
 */
export function groupVideosByTag(videos: VideoRow[]): VideoCategory[] {
  const byTag = new Map<string, VideoRow[]>();
  const push = (key: string, v: VideoRow) => {
    const list = byTag.get(key) ?? [];
    list.push(v);
    byTag.set(key, list);
  };
  for (const v of videos) {
    if (v.tags.length === 0) push('untagged', v);
    for (const t of v.tags) push(t, v);
  }
  const out: VideoCategory[] = [];
  for (const tag of VIDEO_TAGS) {
    const vids = byTag.get(tag);
    if (vids && vids.length) out.push({ tag, label: TAG_LABEL[tag], videos: vids });
  }
  const untagged = byTag.get('untagged');
  if (untagged && untagged.length) {
    out.push({ tag: 'untagged', label: 'Untagged', videos: untagged });
  }
  return out;
}
